/**
 * usePwaInstall — PWA 설치 프롬프트 공유 훅
 * - 모듈 로드 시 beforeinstallprompt 이벤트를 한 번만 캡처
 * - 여러 컴포넌트가 동일한 deferredPrompt를 공유
 * - install() 호출 시 즉시 PWA 설치 프롬프트 표시
 */

import { useState, useEffect, useCallback } from "react";

type DeferredPrompt = Event & {
  prompt: () => void;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

let _deferredPrompt: DeferredPrompt | null = null;
const _subscribers = new Set<() => void>();

const _isStandalone = (): boolean => {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as any).standalone === true
  );
};

const _isSafari = (): boolean => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  return /Safari/.test(ua) && !/Chrome|CriOS|Edg|OPR/.test(ua);
};

const _isiOS = (): boolean => {
  if (typeof navigator === "undefined") return false;
  return /iPhone|iPad|iPod/.test(navigator.userAgent);
};

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e: Event) => {
    e.preventDefault();
    _deferredPrompt = e as DeferredPrompt;
    _subscribers.forEach((fn) => fn());
  });
  window.addEventListener("appinstalled", () => {
    _deferredPrompt = null;
    _subscribers.forEach((fn) => fn());
  });
}

export function usePwaInstall() {
  const [, force] = useState(0);

  useEffect(() => {
    const cb = () => force((v) => v + 1);
    _subscribers.add(cb);
    return () => {
      _subscribers.delete(cb);
    };
  }, []);

  const install = useCallback(async (): Promise<boolean> => {
    if (!_deferredPrompt) return false;
    _deferredPrompt.prompt();
    const result = await _deferredPrompt.userChoice;
    if (result.outcome === "accepted") {
      _deferredPrompt = null;
      _subscribers.forEach((fn) => fn());
      return true;
    }
    return false;
  }, []);

  const standalone = _isStandalone();
  const safari = _isSafari();
  const ios = _isiOS();

  return {
    canPrompt: !!_deferredPrompt && !standalone,
    isStandalone: standalone,
    isSafari: safari,
    isIOS: ios,
    needsManualInstall: !_deferredPrompt && !standalone && (safari || ios),
    install,
  };
}
